/**
 * Maintenance-log store — the per-site maintenance entries of the site entity,
 * lazy-loaded when the Maintenance blade opens.
 *
 * Unlike the rest of the site entity (seeded by the composite retrieve), the
 * maintenance list is fetched on demand from
 * GET /api/odl-app/sites/<siteId>/maintenance-logs/ and stored as plain JS
 * arrays keyed by site id. Create/edit from the blade optimistically prepend or
 * patch an entry before the next full fetch. Does not fan out into the legacy
 * SiteMaintenanceLog entity store.
 *
 * Contains:
 * - siteMaintenanceLogs slice
 * - loadMaintenanceLogs thunk
 * - selectMaintenanceLogs / selectMaintenanceLogsStatus selectors
 * - prependEntry / patchEntry optimistic reducers
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fetchSiteMaintenanceLog } from '@sophocles/api/siteDashboard';
import { extractThunkError } from '@sophocles/api/thunkUtils';

export const loadMaintenanceLogs = createAsyncThunk('siteMaintenanceLogs/load', async (siteId, { rejectWithValue }) => {
  try {
    const data = await fetchSiteMaintenanceLog(siteId);
    // The endpoint may return { results: [...] } or a plain array.
    const entries = Array.isArray(data) ? data : (data?.results ?? []);
    return { siteId: String(siteId), entries };
  } catch (e) {
    return rejectWithValue(extractThunkError(e));
  }
});

const slice = createSlice({
  name: 'siteMaintenanceLogs',
  initialState: {
    /**
     * Per-site load state keyed by String(siteId).
     * @type {{ [siteId: string]: { loading: boolean, error: string|null } }}
     */
    bySiteId: {},
    /**
     * Per-site entries keyed by String(siteId).
     * @type {{ [siteId: string]: object[] }}
     */
    entriesBySiteId: {},
  },
  reducers: {
    /**
     * Optimistically prepend a newly-created entry so the blade reflects the
     * save immediately before the next full fetch.
     */
    prependEntry(state, action) {
      const { siteId, entry } = action.payload;
      const key = String(siteId);
      if (!state.entriesBySiteId[key]) state.entriesBySiteId[key] = [];
      state.entriesBySiteId[key] = [entry, ...state.entriesBySiteId[key]];
    },
    /**
     * Apply a patch to an existing entry in the local store.
     */
    patchEntry(state, action) {
      const { siteId, id, patch } = action.payload;
      const key = String(siteId);
      const entries = state.entriesBySiteId[key];
      if (!entries) return;
      state.entriesBySiteId[key] = entries.map(e => (e.id === id ? { ...e, ...patch } : e));
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadMaintenanceLogs.pending, (state, action) => {
        const key = String(action.meta.arg);
        state.bySiteId[key] = { loading: true, error: null };
      })
      .addCase(loadMaintenanceLogs.fulfilled, (state, action) => {
        const key = action.payload.siteId;
        state.bySiteId[key] = { loading: false, error: null };
        state.entriesBySiteId[key] = action.payload.entries;
      })
      .addCase(loadMaintenanceLogs.rejected, (state, action) => {
        const key = String(action.meta.arg);
        state.bySiteId[key] = { loading: false, error: action.payload ?? 'Failed to load maintenance logs' };
      });
  },
});

// ── Selectors ────────────────────────────────────────────────────────────────

/**
 * @param {string|number} siteId
 * @returns {function} selector returning { loading, error }
 */
export const selectMaintenanceLogsStatus = siteId => state =>
  state.siteMaintenanceLogs.bySiteId[String(siteId)] ?? { loading: false, error: null };

// Stable empty-array reference (see selectSiteAlarms) so an unloaded site
// doesn't return a fresh [] each call and re-render the grid/blade.
const EMPTY_ENTRIES = Object.freeze([]);
/**
 * @param {string|number} siteId
 * @returns {function} selector returning plain JS entry array (empty if not loaded)
 */
export const selectMaintenanceLogs = siteId => state =>
  state.siteMaintenanceLogs.entriesBySiteId[String(siteId)] ?? EMPTY_ENTRIES;

export const { prependEntry, patchEntry } = slice.actions;

export default slice.reducer;
